import React, { useRef, useState } from "react";
import { FiUploadCloud, FiX, FiRefreshCw } from "react-icons/fi";

const MAX_SIZE_MB = 2;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];

// Drag-and-drop / click-to-browse logo uploader with a live preview tile.
// Keeps validation client-side only (type/size) — the server re-validates
// via multer's fileFilter/limits in tenant-theme.controller.js regardless.
const LogoUploader = ({ preview, fileName, onSelect, onRemove }) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  const validateAndSelect = (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Only PNG, JPG, SVG, or WEBP files are allowed.");
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File must be under ${MAX_SIZE_MB}MB.`);
      return;
    }
    setError("");
    onSelect(file);
  };

  const handleInputChange = (event) => {
    validateAndSelect(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    validateAndSelect(event.dataTransfer.files?.[0]);
  };

  return (
    <div className="flex flex-col gap-1">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        onChange={handleInputChange}
        className="hidden"
      />

      {preview ? (
        <div className="relative w-fit rounded-xl border border-canvas-tersery/40 p-3">
          <img
            src={preview}
            alt="Logo preview"
            className="h-24 w-24 object-contain rounded-lg bg-canvas-userbar"
          />
          <div className="absolute top-1 right-1 flex gap-1 bg-canvas-white/90 rounded-full shadow-sm">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              aria-label="Replace logo"
              title="Replace"
              className="p-1.5 rounded-full text-canvas-primary hover:bg-canvas-primary/10 transition-colors"
            >
              <FiRefreshCw className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label="Remove logo"
              title="Remove"
              className="p-1.5 rounded-full text-canvas-danger hover:bg-canvas-danger/10 transition-colors"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>
          {fileName && (
            <span className="block mt-1 font-outfit text-[11px] text-canvas-black max-w-[96px] truncate">
              {fileName}
            </span>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center gap-1 w-48 h-24 rounded-xl border-2 border-dashed transition-colors ${
            isDragging
              ? "border-canvas-primary bg-canvas-primary/5"
              : "border-canvas-tersery/50 hover:border-canvas-primary/50"
          }`}
        >
          <FiUploadCloud className="w-5 h-5 text-canvas-primary" />
          <span className="font-outfit text-xs text-canvas-directoryText text-center px-2">
            Click or drag a logo here
          </span>
        </button>
      )}

      <span className="font-outfit text-[11px] text-canvas-directoryText">
        PNG, JPG, SVG, or WEBP. Max {MAX_SIZE_MB}MB.
      </span>
      {error && (
        <span className="font-outfit text-[11px] text-canvas-danger">{error}</span>
      )}
    </div>
  );
};

export default LogoUploader;
